import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Square, CheckSquare, FileText } from 'lucide-react';
import { money, toneFor, confidenceColors } from '../../lib/theme';

/**
 * Presentation-only. Owns no state. Behavior identical to the previous inline
 * table inside pages/Receipts.js.
 */
export default function ReceiptTable({
  items, loading,
  selected, toggleSelect,
  inSelect,
  colors, effective,
}) {
  const navigate = useNavigate();
  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <div
          className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: colors.border, borderTopColor: colors.accent }}
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3" data-testid="receipts-empty">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center border"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <FileText size={28} style={{ color: colors.textMuted }} />
        </div>
        <p className="text-lg font-semibold" style={{ color: colors.text }}>No receipts to review</p>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          Receipts scanned from the mobile app will appear here.
        </p>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: colors.surface, borderColor: colors.border }}
      data-testid="receipts-list"
    >
      {/* Table header */}
      <div
        className="hidden sm:grid grid-cols-[44px_1fr_120px_100px_100px_80px] gap-2 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider border-b"
        style={{ color: colors.textMuted, borderColor: colors.border }}
      >
        <span></span>
        <span>Vendor</span>
        <span>Date</span>
        <span>Category</span>
        <span className="text-right">Total</span>
        <span className="text-right">Conf.</span>
      </div>

      {items.map((item, idx) => {
        const tone = toneFor(item.min_confidence);
        const { fg: dotColor } = confidenceColors(tone, effective === 'dark');
        const isSelected = selected.has(item.id);
        const pct = Math.round(item.min_confidence * 100);

        return (
          <div
            key={item.id}
            data-testid={`receipt-row-${item.id}`}
            role="button"
            tabIndex={0}
            className="group sm:grid sm:grid-cols-[44px_1fr_120px_100px_100px_80px] gap-2 px-4 py-3 items-center cursor-pointer transition-colors hover:bg-black/[0.03]"
            style={{
              borderBottom: idx < items.length - 1 ? `1px solid ${colors.border}` : 'none',
              backgroundColor: isSelected ? colors.accentSoft : 'transparent',
            }}
            onClick={() => inSelect ? toggleSelect(item.id) : navigate(`/app/receipts/${item.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (inSelect) toggleSelect(item.id); else navigate(`/app/receipts/${item.id}`);
              }
            }}
            onContextMenu={(e) => { e.preventDefault(); toggleSelect(item.id); }}
          >
            {/* Select / Confidence col */}
            <div className="hidden sm:flex items-center justify-center">
              {inSelect ? (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelect(item.id); }}
                  className="focus-visible:outline-none"
                >
                  {isSelected ? (
                    <CheckSquare size={18} style={{ color: colors.accent }} />
                  ) : (
                    <Square size={18} style={{ color: colors.textMuted }} />
                  )}
                </button>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full mb-1" style={{ backgroundColor: dotColor }} />
                  <span className="text-[10px] font-bold tabular-nums" style={{ color: colors.textMuted }}>
                    {pct}%
                  </span>
                </div>
              )}
            </div>

            {/* Vendor + chips */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold truncate" style={{ color: colors.text }}>
                {item.vendor?.value ?? 'Unknown vendor'}
              </span>
              {item.source === 'email' && (
                <span
                  data-testid={`email-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.accentSoft, color: colors.accent }}
                  title="Received via email"
                >
                  📧 EMAIL
                </span>
              )}
              {item.source === 'manual' && (
                <span
                  data-testid={`manual-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.accentSoft, color: colors.accent }}
                  title="Entered manually"
                >
                  ✏️ MANUAL
                </span>
              )}
              {item.category && !item.category_confirmed && (
                <span
                  data-testid={`needs-review-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.warningSoft, color: colors.warning }}
                  title="Auto-categorized — needs review"
                >
                  ⚠ REVIEW
                </span>
              )}
              {item.potential_asset && !item.asset_dismissed && (
                <span
                  data-testid={`asset-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.warningSoft, color: colors.warning }}
                  title={`Possible ${item.asset_type || 'asset'} purchase — review category`}
                >
                  💼 ASSET
                </span>
              )}
              {item.has_splits && (
                <span
                  data-testid={`split-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.accentSoft, color: colors.accent }}
                  title={`Split across ${item.splits_summary?.count || 0} categories`}
                >
                  ✂️ SPLIT
                </span>
              )}
              {item.has_duplicate && (
                <span
                  data-testid={`dup-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: colors.warningSoft, color: colors.warning }}
                >
                  DUP
                </span>
              )}
              {item.qb_publish?.status === 'published' && (
                <span
                  data-testid={`qb-chip-${item.id}`}
                  className="shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: '#E7F7EE', color: '#117A3C' }}
                  title={`Published to QuickBooks on ${item.qb_publish.published_at?.slice(0, 10) || ''}`}
                >
                  QB
                </span>
              )}
              {item.notes && (
                <FileText size={12} className="shrink-0" style={{ color: colors.textMuted }} />
              )}
            </div>

            {/* Date */}
            <div
              className="text-sm tabular-nums hidden sm:block"
              style={{ color: colors.textMuted }}
            >
              {item.date?.value ?? '—'}
            </div>

            {/* Category */}
            <div
              className="text-sm truncate hidden sm:block"
              style={{ color: colors.textMuted }}
            >
              {item.category ?? '—'}
            </div>

            {/* Total */}
            <div
              className="text-sm font-semibold tabular-nums text-right hidden sm:block"
              style={{ color: colors.text }}
            >
              {money(item.total?.value)}
            </div>

            {/* Confidence + status */}
            <div className="hidden sm:flex items-center justify-end gap-1.5">
              {item.status !== 'pending' && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: item.status === 'approved' ? colors.successSoft : colors.errorSoft,
                    color: item.status === 'approved' ? colors.success : colors.error,
                  }}
                >
                  {item.status}
                </span>
              )}
            </div>

            {/* Mobile layout */}
            <div className="sm:hidden flex items-center justify-between mt-1">
              <span className="text-xs tabular-nums" style={{ color: colors.textMuted }}>
                {item.date?.value ?? '—'}{item.category ? ` · ${item.category}` : ''}
              </span>
              <span className="text-sm font-semibold tabular-nums" style={{ color: colors.text }}>
                {money(item.total?.value)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
